import { Component, Inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationFacade, Leg } from '@ridango/components';
import {
    DisruptionMessage,
    DisruptionMessageEntityIcon,
    MessageTypeService,
    AlertUtil,
    ProcessedUrl,
    DisruptionMessageSeverity
} from '@ridango/core';
import Env from '@ridango/core/env';

@Component({
    selector: 'rwidget-disruption-message-details',
    templateUrl: './disruption-message-details.component.html',
    styleUrls: ['./disruption-message-details.component.scss']
})
export class DisruptionMessageDetailsComponent implements OnChanges, OnInit {
    @Input() disruptionMessage: DisruptionMessage;
    @Input() leg: Leg;
    DisruptionMessageSeverity = DisruptionMessageSeverity;
    assetsPath$ = this.applicationFacade.assetsPath$;
    isContrast$ = this.applicationFacade.highContrast$;
    processedUrls: ProcessedUrl[];
    messageLines: { name: string; icon?: DisruptionMessageEntityIcon }[];
    isBusDisruption: boolean;
    isStopDisruption: boolean;
    isBusAndStopDisruption: boolean;
    messageStops: string;
    headerText: string;
    descriptionText: string;
    public selectedImageConfig: ProcessedUrl;
    @Input() modalRef: HTMLIonModalElement;

    constructor(
        private modalCtrl: ModalController,
        public translateService: TranslateService,
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        private messageTypeService: MessageTypeService
    ) {}

    ngOnInit(): void {
        this.processInputs();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.disruptionMessage &&
            changes.disruptionMessage.currentValue !== changes.disruptionMessage.previousValue
        ) {
            this.processInputs();
        }
    }

    private processInputs(): void {
        this.processAlertUrls();
        this.getMessageLines();
        this.isBusDisruptionCheck();
        this.isStopDisruptionCheck();
        this.isBusAndStopDisruptionCheck();
        this.getMessageStops();
        this.getMessageHeaderText();
        this.getMessageDescriptionText();
    }

    async cancel(): Promise<void> {
        // Dismisses this modal when ref if passed
        if (this.modalRef) {
            await this.modalRef.dismiss(null, 'cancel');
        } else {
            // Dismisses any modal that is open.
            // If more than one modal is open, the first one in DOM will be dismissed
            await this.modalCtrl.dismiss(null, 'cancel');
        }
    }

    processAlertUrls() {
        if (!this.disruptionMessage?.alertUrls) {
            return;
        }

        this.processedUrls = this.disruptionMessage.alertUrls.map((url) => {
            return AlertUtil.processAlertUrl(url);
        });
    }

    getMessageLines(): void {
        if (!this.disruptionMessage?.informedEntities) {
            return;
        }

        this.messageLines = this.disruptionMessage.informedEntities
            .filter((entity) => entity?.type === 'ROUTE' && entity?.routeShortName?.trim())
            .map((entity) => ({ name: entity.routeShortName.trim(), icon: entity.icon }))
            .filter((line, index, self) => self.findIndex((l) => l.name === line.name) === index);
    }

    getMessageStops(): void {
        if (!this.disruptionMessage?.informedEntities) {
            return;
        }

        const stops = this.disruptionMessage.informedEntities
            .map((entity) => entity.stopName?.trim())
            .filter((value) => !!value)
            .filter((value, index, self) => self.indexOf(value) === index);

        this.messageStops = stops.join(', ');
    }

    isBusDisruptionCheck(): void {
        this.isBusDisruption = this.messageTypeService.isBusDisruption(this.disruptionMessage);
    }

    isStopDisruptionCheck() {
        this.isStopDisruption = this.messageTypeService.isStopDisruption(this.disruptionMessage);
    }

    isBusAndStopDisruptionCheck() {
        this.isBusAndStopDisruption = this.messageTypeService.isBusAndStopDisruption(this.disruptionMessage);
    }

    getMessageHeaderText() {
        const translation = this.disruptionMessage.headerText?.find(
            (translation) => translation.language === this.translateService.currentLang
        );

        this.headerText = translation ? translation.text : this.disruptionMessage.headerTextDefault;
    }

    getMessageDescriptionText() {
        const translation = this.disruptionMessage.descriptionText?.find(
            (translation) => translation.language === this.translateService.currentLang
        );

        this.descriptionText = translation ? translation.text : this.disruptionMessage.descriptionTextDefault;
    }

    openImageFullScreen(url: ProcessedUrl) {
        this.selectedImageConfig = url;
    }

    closeSelectedImageModal() {
        this.selectedImageConfig = null;
    }
}
